import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import _ from 'lodash';

import { formatServerDateTime, formatDisplayDate, parseDisplayDate } from '@/util/MomentUtils';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showErrorMessage from '@/util/showErrorMessage';
import { validateForm } from '@/util/ParsleyUtils';

import SaveCustomerOrderGeneral from '../../../services/SaveCustomerOrderGeneral';

import tmplMFDGeneralView from './MFDGeneralView.hbs';
import './MFDGeneralView.css';

const MFDGeneralView = View.extend({
  template: tmplMFDGeneralView,

  className: 'mfd-general-view',

  ui: {
    currentContractExpDate: 'input[name="currentContractExpDate"]',
    completedAssessmentDate: 'input[name="completedAssessmentDate"]',
    dateEquipmentReq: 'input[name="dateEquipmentReq"]',
    hertzAlert: '.hertz-error',
    voltsAlert: '.volts-error',
    ampsAlert: '.amps-error',
    plugTypeAlert: '.plug-type-error',
    commandPOC: '#commandPOC',
    generalForm: '.general-form',
    firstName: '#firstName',
    lastName: '#lastName',
    email: '#email',
    telephone: '#telephone'
  },

  events: {
    'change @ui.commandPOC': 'onCommandPOCChange'
  },

  onRender() {
    const order = this.model.get('order');

    this.ui.currentContractExpDate.datepicker({ format: 'mm/dd/yyyy' });
    if (order.currentContractExpDate) {
      this.ui.currentContractExpDate.datepicker(
        'setDate',
        formatDisplayDate(order.currentContractExpDate)
      );
    }

    this.ui.completedAssessmentDate.datepicker({ format: 'mm/dd/yyyy' });
    if (order.completedAssessmentDate) {
      this.ui.completedAssessmentDate.datepicker(
        'setDate',
        formatDisplayDate(order.completedAssessmentDate)
      );
    }

    this.ui.dateEquipmentReq.datepicker({ format: 'mm/dd/yyyy' });
    if (order.dateEquipmentReq) {
      this.ui.dateEquipmentReq.datepicker('setDate', formatDisplayDate(order.dateEquipmentReq));
    }

    order.hasReq = order.hasSpecialAccessReq;
    order.accessNotes = order.specialAccessReqNotes;
  },

  onCommandPOCChange() {
    const commandPOCs = this.model.get('orderDetails').commandPOCs;
    const commandId = this.ui.commandPOC.val();

    if (commandId) {
      const command = _.find(commandPOCs, { id: commandId });
      this.$('#firstName').val(`${command.firstName}`);
      this.$('#lastName').val(`${command.lastName}`);
      this.$('#email').val(command.email);
      this.$('#telephone').val(command.telephone);
    } else {
      this.$('#firstName').val('');
      this.$('#lastName').val('');
      this.$('#email').val('');
      this.$('#telephone').val('');
    }
    validateForm(this.ui.generalForm, 'commandPOCInfo');
  },

  validateNumber(val) {
    if (!/^\d*\.?\d*$/.test(val)) {
      return false;
    }
    return true;
  },

  async validate() {
    const isReadOnly = this.model.get('isReadOnly');
    const serialFormData = Syphon.serialize(this.ui.generalForm);
    if (!this.validatePowerSource(serialFormData)) return false;
    try {
      if (isReadOnly) this.ui.generalForm.find(':input').prop('disabled', false);
      await validateForm(this.ui.generalForm);
      this.trigger('no:errors');
      return true;
    } catch {
      if (isReadOnly) {
        this.ui.generalForm.find(':input').prop('disabled', true);
      }
      this.trigger('has:errors');
      return false;
    }
  },

  validatePowerSource(formData) {
    let isValid = true;
    if (formData.powerSourceVolts) {
      if (!this.validateNumber(formData.powerSourceVolts)) {
        this.ui.voltsAlert.slideDown(300);
        isValid = false;
      }
    }
    if (formData.powerSourceHertz) {
      if (!this.validateNumber(formData.powerSourceHertz)) {
        this.ui.hertzAlert.slideDown(300);
        isValid = false;
      }
    }
    if (formData.powerSourceAmps) {
      if (!this.validateNumber(formData.powerSourceAmps)) {
        this.ui.ampsAlert.slideDown(300);
        isValid = false;
      }
    }
    if (formData.powerSourcePlugType) {
      if (!this.validateNumber(formData.powerSourcePlugType)) {
        this.ui.plugTypeAlert.slideDown(300);
        isValid = false;
      }
    }
    isValid ? this.trigger('no:errors') : this.trigger('has:errors');
    return isValid;
  },

  hideValidation() {
    this.ui.voltsAlert.hide();
    this.ui.hertzAlert.hide();
    this.ui.ampsAlert.hide();
    this.ui.plugTypeAlert.hide();
  },

  async onAttach() {
    if (this.model.get('orderDetails').showValidation) {
      await this.validate();
    }
  },

  async onSave() {
    this.hideValidation();
    const serialFormData = Syphon.serialize(this);
    const powerSourceValidation = this.validatePowerSource(serialFormData);
    if (serialFormData.completedAssessmentDate) {
      const completedAssessmentDate = formatServerDateTime(parseDisplayDate(serialFormData.completedAssessmentDate));
      serialFormData.completedAssessmentDate = completedAssessmentDate;
    }
    if (serialFormData.currentContractExpDate) {
      const currentContractExpDate = formatServerDateTime(parseDisplayDate(serialFormData.currentContractExpDate));
      serialFormData.currentContractExpDate = currentContractExpDate;
    }
    if (serialFormData.dateEquipmentReq) {
      const dateEquipmentReq = formatServerDateTime(parseDisplayDate(serialFormData.dateEquipmentReq));
      serialFormData.dateEquipmentReq = dateEquipmentReq;
    }

    if (!powerSourceValidation) {
      this.showSaveError();
      return;
    }
    const formData = {
      generalFormData: serialFormData,
      orderId: this.model.get('orderDetails').orderId,
      orderTypeId: this.model.get('orderDetails').orderTypeId
    };

    try {
      await SaveCustomerOrderGeneral(formData);

      Object.assign(this.model.get('order'), {
        ...serialFormData,
        hasReq: serialFormData.hasSpecialAccessReq,
        accessNotes: serialFormData.specialAccessReqNotes
      });

      this.showSaveSuccess();
    } catch (err) {
      this.showSaveError();
      throw new Error(err);
    }
  },

  showSaveError() {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage:
        'An error occured while saving general information for this order. Try again or contact an administrator if your issue persists.'
    });
  },

  showSaveSuccess() {
    showSuccessMessage({
      code: 'saveSuccess',
      title: 'Save Success',
      message: 'This order has been saved successfully.',
      hideAfter: 5
    });
  }
});

export default MFDGeneralView;
